import React, { useState, useEffect } from 'react';
import './trans.css';
import logo from './img/edaplogo.png';
import html2pdf from 'html2pdf.js';
import { XCircle } from 'react-feather';
import { getDatabase, ref, set, onValue, remove } from 'firebase/database';
import app from './firebase.js';

const Transcript = () => {
  const [formData, setFormData] = useState({
    name: '',
    matriculationNumber: '',
    class: '',
    faculty: '',
    department: '',
    year: '',
    programType: '', // Changed to empty string
    verdict: '',     // Changed to empty string
    image: null,
    courses: [{ code: '', title: '', credit: '', grade: '' }],
  });

  const [transcripts, setTranscripts] = useState([]);
  const [students, setStudents] = useState([]);

  const db = getDatabase(app);

  useEffect(() => {
    // Read transcripts from Firebase on load
    const transcriptRef = ref(db, '/transcripts');
    onValue(transcriptRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const transcriptsArray = Object.keys(data).map((key) => ({
          id: key, // Unique ID for each transcript
          ...data[key],
        }));
        setTranscripts(transcriptsArray);
      } else {
        setTranscripts([]);
      }
    });
  }, [db]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleCourseChange = (index, e) => {
    const { name, value } = e.target;
    const updatedCourses = [...formData.courses];
    updatedCourses[index][name] = value;
    setFormData((prevData) => ({
      ...prevData,
      courses: updatedCourses,
    }));
  };

  const addCourse = () => {
    setFormData((prevData) => ({
      ...prevData,
      courses: [...prevData.courses, { code: '', title: '', credit: '', grade: '' }],
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData((prevData) => ({
          ...prevData,
          image: reader.result,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newTranscriptHTML = generateTranscriptHTML();

    const newTranscript = {
      name: formData.name,
      matriculationNumber: formData.matriculationNumber,
      class: formData.class,
      faculty: formData.faculty,
      department: formData.department,
      year: formData.year,
      programType: formData.programType,
      verdict: formData.verdict,
      image: formData.image,
      courses: formData.courses,
      transcript: newTranscriptHTML,
    };

    // Save transcript in Firebase
    const transcriptRef = ref(db, `/transcripts`);
    const newRef = ref(db, `/transcripts/${formData.matriculationNumber}`);
    set(newRef, newTranscript)
      .then(() => console.log('Transcript saved successfully in Firebase'))
      .catch((error) => console.error('Error saving transcript:', error));

    // Reset form
    setFormData({
      name: '',
      matriculationNumber: '',
      class: '',
      faculty: '',
      department: '',
      year: '',
      image: null,
      courses: [{ code: '', title: '', credit: '', grade: '' }],
    });
  };

  const generateTranscriptHTML = () => {
    const courseGPAs = formData.courses.map((course) => {
      const gradePercentage = course.grade / 100;
      return gradePercentage * 4 * course.credit;
    });

    const totalCredits = formData.courses.reduce(
      (sum, course) => sum + Number(course.credit),
      0
    );
    const totalGrades = formData.courses.reduce(
      (sum, course) => sum + Number(course.grade),
      0
    );
    const averageGrade = totalGrades / formData.courses.length;
    const totalGPA = courseGPAs.reduce((sum, gpa) => sum + gpa, 0) / totalCredits;

    const currentDate = new Date();
    const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
    const formattedDate = currentDate.toLocaleDateString('fr-FR', options);
    const dateString = `Lisala, le ${formattedDate}`;
    const programTitle = formData.programType ? formData.programType.toUpperCase() : '';

    return `
    <!DOCTYPE html>
    <html lang="fr">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>Bulletin de notes de ${formData.programType}</title>
      <style>
          body { font-family: Arial, sans-serif; margin: 0; padding: 20px; background-color: #f9f9f9; }
          .transcript-container { width: 800px; margin: 0 auto; background-color: #fff; padding: 20px; border: 1px solid #ddd; border-radius: 5px; }
          .header { margin-bottom: 20px; }
          .school-info { display: flex; font-family: monospace; }
          .school-info img { width: 180px; height: auto; margin-right: 10px; }
          .student-info {display: flex; justify-content: space-between; flex-direction: row; }
          .transcript-table { width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 14px; }
          .transcript-table th, .transcript-table td { padding: 8px; text-align: right; border: 1px solid #000; }
          .transcript-table .year-info-cell { font-weight: bold; text-align: center; vertical-align: middle; }
          .total-gpa { margin-top: 10px; text-align: left; font-weight: bold; }
          .student-image { width: 140px; margin-bottom: 20px; }
          .footer-verdict { margin-top: 30px; display: flex; justify-content: space-between; align-items: flex-start; }
      </style>
    </head>
    <body>
      <div class="transcript-container">
        <div class="header">
            <div class="school-info">
              <img src="${logo}" alt="Logo de l'Université">
              <div>
                <h2>UNIVERSITE DE LISALA</h2>
                <p style="font-size: 15px">Centre Interuniversitaire de Recherche Pluridisciplinaire (CIREP)</p>
                <p>Email: info@universitedelisala.ac.cd<br/>
                Site web: universitedelisala.ac.cd</p>
                <h3 style="text-align: center; text-transform: uppercase; margin-top: 20px;">BULLETIN DE NOTES DE ${programTitle}</h3>
              </div>
            </div>
        </div>
        <div class="student-info">
          <div>
            <p><strong>Nom:</strong> ${formData.name}</p>
            <p><strong>Matricule:</strong> ${formData.matriculationNumber}</p>
            <p><strong>Faculté:</strong> ${formData.faculty}</p>
            <p><strong>Département:</strong> ${formData.department}</p>
          </div>
          <div>
            ${formData.image ? `<img src="${formData.image}" alt="Student Image" class="student-image" />` : ''}
          </div>
        </div>
        <table class="transcript-table">
          <thead>
            <tr>
              <th>Année Academique</th>
              <th>Code</th>
              <th>Titre des cours</th>
              <th>Crédit</th>
              <th>Notes/100</th>
              <th>GPA</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td rowspan="${formData.courses.length}" class="year-info-cell"> ${formData.class}</br>${formData.year}</td>
              <td>${formData.courses[0].code}</td>
              <td>${formData.courses[0].title}</td>
              <td style="text-align: right;">${formData.courses[0].credit}</td>
              <td style="text-align: right;">${formData.courses[0].grade}</td>
              <td style="text-align: right;">${(courseGPAs[0] / formData.courses[0].credit).toFixed(2)}</td>
            </tr>
            ${formData.courses.slice(1).map((course, index) => `
              <tr>
                <td>${course.code}</td>
                <td>${course.title}</td>
                <td style="text-align: right;">${course.credit}</td>
                <td style="text-align: right;">${course.grade}</td>
                <td style="text-align: right;">${(courseGPAs[index + 1] / course.credit).toFixed(2)}</td>
              </tr>
            `).join('')}
            <tr>
              <td colspan="3"><strong>Total</strong></td>
              <td>${totalCredits}</td>
              <td>${averageGrade.toFixed(2)}</td>
              <td>${totalGPA.toFixed(2)}</td>
            </tr>
          </tbody>
        </table>
        <div class="footer-verdict">
             <div>
               <p><strong>Verdict:</strong> ${formData.verdict}</p>
             </div>
             <div>
               <p>${dateString}</p>
             </div>
        </div>
      </div>
    </body>
    </html>
    `;
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
const [selectedTranscript, setSelectedTranscript] = useState(null);


 const handleTranscriptClick = (index) => {
    setSelectedTranscript(transcripts[index]);
    setIsModalOpen(true);
  };

  const handleClickOutside = (e) => {
    if (e.target.classList.contains('modal-overlay')) {
      setIsModalOpen(false);
      setSelectedTranscript(null);
    }
  };

  useEffect(() => {
    if (isModalOpen) {
      document.addEventListener('click', handleClickOutside);
    } else {
      document.removeEventListener('click', handleClickOutside);
    }
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isModalOpen]);


  const handleDeleteTranscript = (matriculationNumber) => {
    const transcriptRef = ref(db, `/transcripts/${matriculationNumber}`);

    remove(transcriptRef)
      .then(() => console.log(`Transcript with Matricule ${matriculationNumber} deleted successfully`))
      .catch((error) => console.error('Error deleting transcript:', error));
  };


  const handleDownloadPDF = (index) => {
    const transcriptHTML = transcripts[index].transcript;
    const element = document.createElement('div');
    element.innerHTML = transcriptHTML;

    const options = {
      filename: 'transcript.pdf',
      html2canvas: { x: -30, y: -20, scale: 2 , width: 900},
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
    };

    html2pdf()
      .from(element)
      .set(options)
      .save();
  };

  return (
    <div className="transcript-wrapper">
      <div className="form-blt">
        <img src={logo} className="form-logo" alt="Logo" />
        <form onSubmit={handleSubmit} className='trans-form' >
          {/* Form Inputs */}
          <div>
            <input
              type="text"
              name="name"
              placeholder="Noms"
              value={formData.name}
              onChange={handleInputChange}
              required
            />
          </div>
          <div>
            <input
              type="text"
              name="faculty"
              placeholder="Faculté"
              value={formData.faculty}
              onChange={handleInputChange}
              required
            />
          </div>
          <div>
            <input
              type="text"
              name="department"
              placeholder="Département"
              value={formData.department}
              onChange={handleInputChange}
              required
            />
          </div>
          <div>
            <input
              type="text"
              name="matriculationNumber"
              placeholder="Matricule"
              value={formData.matriculationNumber}
              onChange={handleInputChange}
              required
            />
          </div>
          <div>
            <input
              type="text"
              name="class"
              placeholder="Promotion"
              value={formData.class}
              onChange={handleInputChange}
              required
            />
          </div>
          <div>
            <input
              type="text"
              name="year"
              placeholder="Année Académique"
              value={formData.year}
              onChange={handleInputChange}
              required
            />
          </div>
          <div>
            <select
              name="programType"
              value={formData.programType}
              onChange={handleInputChange}
              required
              style={{
                width: '500px',
                padding: '10px',
                marginBottom: '10px',
                marginRight: '5px',
                border: '1px solid #7a7a7a',
                borderRadius: '2px',
                backgroundColor: '#fff'
              }}
            >
              <option value="" disabled>Sélectionner le cycle</option>
              <option value="Master">Master</option>
              <option value="Doctorat">Doctorat</option>
            </select>
          </div>
          <div>
            <select
              name="verdict"
              value={formData.verdict}
              onChange={handleInputChange}
              required
               style={{
                width: '500px',
                padding: '10px',
                marginBottom: '10px',
                marginRight: '5px',
                border: '1px solid #7a7a7a',
                borderRadius: '2px',
                backgroundColor: '#fff'
              }}
            >
              <option value="" disabled>Sélectionner le verdict</option>
              <option value="Admis">Admis</option>
              <option value="Ajourné">Ajourné</option>
              <option value="Echoué">Echoué</option>
            </select>
          </div>
          <div>
            <input
              type="file"
              onChange={handleImageChange}
            />
          </div>

          {/* Course Fields */}
          <div>
            {formData.courses.map((course, index) => (
              <div key={index}>
                <br/>
                <input
                  type="text"
                  name="title"
                  placeholder="Titre du cours"
                  value={course.title}
                  onChange={(e) => handleCourseChange(index, e)}
                  required
                />
                <input
                  type="text"
                  name="code"
                  placeholder="Code"
                  value={course.code}
                  onChange={(e) => handleCourseChange(index, e)}
                  required
                />
                <br/>
                <input
                  type="number"
                  name="credit"
                  placeholder="Crédit"
                  value={course.credit}
                  onChange={(e) => handleCourseChange(index, e)}
                  required
                />
                <input
                  type="number"
                  name="grade"
                  placeholder="Note"
                  value={course.grade}
                  onChange={(e) => handleCourseChange(index, e)}
                  required
                />
              </div>
            ))}
          <button 
            className='download-button' 
            type="button" 
            onClick={addCourse}
            style={{
              padding: '10px 20px',
              backgroundColor: '#007bff',
              color: '#fff',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              marginTop: '10px',
              width: '245px'
            }}
          >
            Ajouter un Cours
          </button>
          <button 
            className='download-button' 
            type="submit"
            style={{
              padding: '10px 20px',
              backgroundColor: '#28a745',
              color: '#fff',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              marginTop: '10px',
              width: '245px'
            }}
          >
            Générer le Bulletin
          </button>
          </div>
        </form>
      </div>

      <div className="transcript-output">
        {/* Display generated transcripts */}
        {transcripts.length > 0 && (
          <div>
            <h3 style={{fontFamily: "monospace"}}>Bulletin disponible</h3>
            <div >
              {transcripts.map((transcript, index) => (
                <div className='name-space'>
                  <div key={index}>
                    <button onClick={() => handleTranscriptClick(index)} className='name-tag'>
                      {transcript.name}
                    </button>
                  </div>
                  <div>
                    <button
                      onClick={() => handleDeleteTranscript(transcript.matriculationNumber)}                      className="crud-btn"
                      id='crd-delete'
                    >
                      Supprimer
                    </button>
                    <button
                      onClick={() => handleDownloadPDF(index)}
                      className="crud-btn"
                    >
                      Telecharger
                    </button>

                  </div>
                  </div>
              ))}
            </div>
          </div>
        )}
      </div>
      {isModalOpen && (
  <div className="modal-overlay">
    <div className="modal-content">
      <div dangerouslySetInnerHTML={{ __html: selectedTranscript?.transcript }} />
    </div>
      <button className="close-modal-btn" onClick={() => setIsModalOpen(false)}>
        <XCircle size={28}/>
      </button>
  </div>
)}

    </div>
  );
};

export default Transcript;
